import Immutable from 'immutable';
import { Subscription, SubscriptionProvider } from './SubscriptionProvider';

/**
 * A subscriber that allows access to store.
 * @param  {redux.store}          store   The redux store to use for this subscription.
 * @return {SubscriptionProvider}         The subscription model.
 */
const StateSubscriber = ((store) => {
  /**
   * A function that grants the ability to subscribe to a particular path
   * in store.
   * @param  {string}   path      The path in the store in which to subscribe.
   * @param  {function} onChange  The function which will be called when the store changes.
   * @return {function}           The unsubscribe function.
   */
  function subscribeToStore(path, onChange) {
    let currentState;
    const selector = () => store.getState().getIn(path);

    function handleChange() {
      const nextState = selector();

      if (!Immutable.is(nextState, currentState)) {
        currentState = nextState;

        onChange((() => {
          if (currentState?.toJS) {
            return currentState.toJS();
          }

          return currentState;
        })());
      }
    }

    const unsubscribe = store.subscribe(handleChange);
    handleChange(); // evoke handler on subcription creation
    return unsubscribe;
  }

  /**
   * Creates a subscription object.  The subscription must contain a key,
   * a callback, and an unsubscribe function.
   * @param  {string}   key The path on which to subscribe.
   * @param  {Function} fn  The callback which will be fired when the store
   *                        changes at the supplied key change.
   * @return {Subscription} A subscription object.
   */
  const buildSubscriptionFn = (fn, key) => {
    const unsubscribe = subscribeToStore(key, fn);

    return new Subscription({
      key,
      callback: fn,
      unsubscribeFn: unsubscribe,
    });
  };

  // create a subscriptionProvider, and provide it with the buildSubscriptionFn
  return new SubscriptionProvider({ buildSubscriptionFn });
});

export default StateSubscriber;
